// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import StatCard from "@/UI/cards/StatCard";
// --- Logic --------------------------------------------------------------------------------------
type TyOverview = { customers: number; new: number; offer: number; won: number };

type Props = {
    /** Bumped by the screen after anything that could move a figure, so the row reloads itself. */
    reloadAt?: number | null;
};

/**
 * The four figures above the CRM, on **both** tabs — the section's state does not change because
 * you walked from the people to the deals, and a row of numbers that appears and disappears as you
 * move between tabs reads as two different sections.
 *
 * Four, not six. A tile is something a person checks; the stage row under the pipeline is something
 * they work. So this answers "how are we doing" — how many people we know, how many enquiries
 * nobody has touched yet, how many reached an offer, how many were won — and `contacted`,
 * `qualified` and `lost` stay on the row below, where they are filters.
 *
 * It has its **own** request rather than reading the list's counts, so filtering the list underneath
 * never moves a number somebody is reporting upwards.
 */
export default function CrmStats({ reloadAt }: Props) {
    const t = useTranslation();
    const overview = useFetch<TyOverview>("/crm/overview");

    useEffect(() => {
        if (!reloadAt) return;
        overview.invoke();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadAt]);

    const data = overview.data;

    return (
        <div className="stat-row">
            <StatCard icon="crm" value={data?.customers ?? 0} label={t.crm.customers} />
            <StatCard icon="add" value={data?.new ?? 0} label={t.crm.stats.new} />
            <StatCard icon="document" value={data?.offer ?? 0} label={t.crm.stats.offer} />
            <StatCard icon="check" value={data?.won ?? 0} label={t.crm.stats.won} />
        </div>
    );
}
